// app/components/approval/ApprovalTabs.tsx
import { useEffect, useState } from "react";
import { t } from "i18next";
import { gxDashboardApprovalsPendingCountApi } from "@/app/apiConfig/services/paymentsAbstractApiLayer";
import type { CountResponse } from "@/app/types/payments";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import type { ApprovalTabConfig, ApprovalTabId } from "../../types/approval";

interface ApprovalTabsProps {
  tabs: ApprovalTabConfig[];
  activeTabId: ApprovalTabId;
  onTabChange: (tabId: ApprovalTabId) => void;
}

// ApprovalTabId -> CountResponse field, per the mapping table in the requirement
const TAB_COUNT_FIELD: Record<ApprovalTabId, keyof CountResponse> = {
  all: "total",
  txn: "countOfTotalTransPay",
  rtp: "countOfRtp",
  bene: "countOfBeneficiary",
  bulk: "countOfBulkpayment",
  payrolls: "countOfBulkpayroll",
};

const ApprovalTabs = ({
  tabs,
  activeTabId,
  onTabChange,
}: ApprovalTabsProps) => {
  // Mirrors ActionCard's getActionCardApi exactly — same API, same call
  // shape (no payload), same error-reset-to-undefined behavior.
  const [countDetails, setCountDetails] = useState<CountResponse>();
  const [loading, setLoading] = useState<boolean>(true);

  const getActionCardApi = async () => {
    setLoading(true);
    try {
      const result = await gxDashboardApprovalsPendingCountApi();
      if (result?.data !== null) {
        setCountDetails(result?.data);
      }
    } catch (error) {
      console.error("ActionCard API failed:", error);
      setCountDetails(undefined);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getActionCardApi();
  }, []);

  // Falls back to the tab's own `count` (as passed by the parent) if the API
  // hasn't resolved yet or failed — keeps tabs visible with a sane value
  // rather than blanking out, per "keep tabs visible" / "existing fallback
  // behavior" in the requirement.
  const resolveTabCount = (tab: ApprovalTabConfig): number => {
    const field = TAB_COUNT_FIELD[tab.id];
    const apiValue = countDetails?.[field];
    return typeof apiValue === "number" ? apiValue : tab.count;
  };

  return (
    <div className="relative">
      {loading && (
        <div className="gx-bene-form-container-loading-overlay">
          <SubmitLoading />
        </div>
      )}

      <div className="gx-approval-tabs" role="tablist">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;

          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`gx-approval-tabs__item${
                isActive ? " gx-approval-tabs__item--active" : ""
              }`}
              onClick={() => onTabChange(tab.id)}
            >
              <span className="gx-approval-tabs__label">{t(tab.labelKey)}</span>
              <span className="gx-approval-tabs__count">
                {String(resolveTabCount(tab)).padStart(2, "0")}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ApprovalTabs;

// // app/components/approval/ApprovalTabs.tsx
// import { t } from "i18next";
// import type { ApprovalTabConfig, ApprovalTabId } from "../../types/approval";

// interface ApprovalTabsProps {
//   tabs: ApprovalTabConfig[];
//   activeTabId: ApprovalTabId;
//   onTabChange: (tabId: ApprovalTabId) => void;
// }

// const ApprovalTabs = ({
//   tabs,
//   activeTabId,
//   onTabChange,
// }: ApprovalTabsProps) => {

//   return (
//     <div className="gx-approval-tabs" role="tablist">
//       {tabs.map((tab) => {
//         const isActive = tab.id === activeTabId;

//         return (
//           <button
//             key={tab.id}
//             type="button"
//             role="tab"
//             aria-selected={isActive}
//             className={`gx-approval-tabs__item${
//               isActive ? " gx-approval-tabs__item--active" : ""
//             }`}
//             onClick={() => onTabChange(tab.id)}
//           >
//              <span className="gx-approval-tabs__label">{t(tab.labelKey)}</span>
//             <span className="gx-approval-tabs__count">
//               {String(tab.count).padStart(2, "0")}
//             </span>
//           </button>
//         );
//       })}
//     </div>
//   );
// };

// export default ApprovalTabs;
